import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { fetchContactById, LeadContact, deleteLeadContact } from '@/services/leadContactsService';
import { AddContactDialog } from '@/components/contacts/AddContactDialog';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import ContactDetail from '@/components/contacts/ContactDetail';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ContactDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [contact, setContact] = useState<LeadContact | null>(null);
  const [lead, setLead] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchContactData = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      const { contact: contactData, lead: leadData } = await fetchContactById(id);
      setContact(contactData);
      setLead(leadData);
    } catch (error) {
      console.error('Error fetching contact:', error);
      toast({
        title: "Error",
        description: "Failed to load contact details",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchContactData();
  }, [id]);

  const handleContactUpdated = () => {
    setIsEditDialogOpen(false);
    fetchContactData();
    toast({
      title: "Success",
      description: "Contact updated successfully",
    });
  };

  const handleDeleteContact = async () => {
    if (!contact) return;

    setIsDeleting(true);
    try {
      await deleteLeadContact(contact.id.toString());
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
      navigate("/app/contacts");
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        title: "Error",
        description: "Failed to delete contact",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  if (!id) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="text-center p-6 border border-dashed rounded-lg">
          <h2 className="text-xl font-bold mb-2">Contact ID Required</h2>
          <p className="text-muted-foreground">No contact ID was provided in the URL.</p>
          <Button onClick={() => navigate('/contacts')} className="mt-4">
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="text-center p-6 border border-dashed rounded-lg">
          <h2 className="text-xl font-bold mb-2">Contact Not Found</h2>
          <p className="text-muted-foreground">The contact with ID {id} could not be found.</p>
          <Button onClick={() => navigate('/contacts')} className="mt-4">
            Back to Contacts
          </Button>
        </div>
      </div>
    );
  }


  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleBack = () => {
    navigate("/app/contacts");
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={handleBack}
          className="border-border"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Contact Details</h1>
      </div>

      <ContactDetail 
        contact={contact}
        onEdit={handleEdit}
        onBack={handleBack}
        onDelete={handleDeleteClick}
      />

      {/* Edit Contact Dialog */}
      <AddContactDialog
        open={isEditDialogOpen}
        onOpenChange={(open) => !open && setIsEditDialogOpen(false)}
        editContact={contact}
        onContactAdded={handleContactUpdated}
        leadCompanyId={contact.lead_company_id?.toString()}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {contact?.first_name} {contact?.last_name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteContact}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContactDetailPage;